/**
 * The studio's live cut session: takes, saving live changes in order, rolling
 * back what could not be saved, and undo. Framework-free; the studio wires it
 * to its annotations state and its save queue.
 *
 * Saving. Every step of a live action is one request, run through the
 * studio's save queue, so requests reach the server strictly in the order the
 * changes were made: a delete never overtakes the add it depends on. A cut
 * added live has a placeholder id until its add returns; requests queued
 * behind it look up the real id when their turn comes.
 *
 * Failures. When a step fails, the rest of its action is not sent, and every
 * step of the action that did not reach the server is reverted locally (a
 * failed add disappears, a failed retarget goes back, a failed delete comes
 * back). Later requests that depended on it are dropped: a request for a cut
 * that was never saved, or undo bringing back a cut whose delete never
 * happened. The undo entry keeps only the steps that were saved.
 */

import type {
  MulticamAnnotation,
  addMulticamAnnotation,
  deleteMulticamAnnotation,
  updateMulticamAnnotation,
} from '../../graphqlService';
import {
  MulticamAnnotationWrite,
  SaveAction,
  draftFromAnnotation,
  normalizeAnnotations,
  sortAnnotations,
  toMutationInput,
} from './multicamStudioModel';
import {
  KeyOf,
  LiveCutPlan,
  LivePressPlan,
  LiveStep,
  LiveTake,
  applyLiveSteps,
  emptyTake,
  invertLiveSteps,
  planLivePress,
  stepsForPlan,
} from './liveCutModel';

/** Undo keeps at least this many live actions. */
export const LIVE_UNDO_LIMIT = 100;

/** Placeholder ids for cuts added live, until the server returns their ids. */
export const LIVE_PLACEHOLDER_PREFIX = 'live-pending-';

export interface LiveCutApi {
  add: typeof addMulticamAnnotation;
  update: typeof updateMulticamAnnotation;
  remove: typeof deleteMulticamAnnotation;
}

/** What the session needs to know about the studio at the moment of a press. */
export interface LiveCutContext {
  multicamSetId: string | undefined;
  fps: number;
  masterKey: string;
  angleKeys: string[];
  /** False for a locked set, or while its cuts and notes are not loaded. */
  editable: boolean;
}

export interface LiveCutSessionDeps {
  getAnnotations: () => MulticamAnnotation[];
  /**
   * Applies a change to the studio's annotations. What `getAnnotations`
   * returns must include it at once, so presses made in quick succession
   * plan on each other's results.
   */
  updateAnnotations: (change: (list: MulticamAnnotation[]) => MulticamAnnotation[]) => void;
  /** Runs a request after every request queued before it. */
  runInOrder: <T>(work: () => Promise<T>) => Promise<T>;
  api: LiveCutApi;
  getContext: () => LiveCutContext;
  onSaveError: (error: unknown, action: SaveAction) => void;
  /** Undo depth or the saving state changed. */
  onChange?: () => void;
}

export interface LivePress {
  angleKey: string;
  /** Seconds on the shared timeline, read from the clock at the press. */
  time: number;
  /** Length of the shared timeline in seconds; 0 when not known. */
  duration: number;
  hasFootage: (angleKey: string, seconds: number) => boolean;
}

export interface LiveCutSession {
  /** Cuts to an angle at the press's frame; returns what happened. */
  press: (press: LivePress) => LivePressPlan;
  /** Reverts the last live action; returns it, or null when there is none. */
  undo: () => LiveCutPlan | null;
  /** Playback started or stopped, or the playhead jumped: the next press starts a new take. */
  endTake: () => void;
  undoDepth: () => number;
  /**
   * Forgets the live actions to undo: the cuts they changed were replaced
   * another way (suggested cuts), so undoing them would bring old cuts back.
   */
  resetUndo: () => void;
  /** True while live changes to this annotation are still being saved. */
  isBusy: (id: string) => boolean;
  keyOf: KeyOf;
}

interface UndoEntry {
  plan: LiveCutPlan;
  /** The steps that were (or are being) saved. */
  steps: LiveStep[];
  takeId: number;
  /** The take as it was before this action, restored when it is undone mid-take. */
  takeBefore: LiveTake;
}

interface Action {
  /** A step failed: the remaining steps are not sent. */
  stopped: boolean;
  /** Steps that reached the server. */
  saved: number;
  /** The undo entry of a live action. */
  entry: UndoEntry | null;
  /** For an undo: the entry it reverts, put back if none of it could be saved. */
  undoOf: UndoEntry | null;
}

interface Job {
  action: Action;
  step: LiveStep;
  /** The step's sequence number for its cut; a later change to the same cut owns its state. */
  seq: number;
  input: MulticamAnnotationWrite | null;
}

type JobOutcome =
  | { type: 'saved'; annotation?: MulticamAnnotation }
  /** stopped: an earlier step of its action failed; unsaved: its cut never reached the server; kept: the cut it brings back was never deleted. */
  | { type: 'skipped'; reason: 'stopped' | 'unsaved' | 'kept' | 'gone' };

const messageOf = (error: unknown) => (error instanceof Error ? error.message : typeof error === 'string' ? error : '');

export const createLiveCutSession = (deps: LiveCutSessionDeps): LiveCutSession => {
  let placeholderCount = 0;
  /** Placeholder key -> the id the server gave the cut. */
  const savedIdOf: Record<string, string> = {};
  /** Server id -> the placeholder key it was added under. */
  const placeholderOf: Record<string, string> = {};
  /** Placeholder keys whose add never reached the server. */
  const unsaved: Record<string, true> = {};
  /** Keys whose delete never reached the server. */
  const notDeleted: Record<string, true> = {};
  const pending: Record<string, number> = {};
  const seqOf: Record<string, number> = {};

  let take: LiveTake = emptyTake();
  let takeId = 0;
  let undoStack: UndoEntry[] = [];

  const keyOf: KeyOf = (id) => placeholderOf[id] ?? id;
  const isPlaceholder = (key: string) => key.indexOf(LIVE_PLACEHOLDER_PREFIX) === 0;
  const newKey = () => {
    placeholderCount += 1;
    return `${LIVE_PLACEHOLDER_PREFIX}${placeholderCount}`;
  };
  const changed = () => deps.onChange?.();

  /** The server id for a key, or null when that cut never reached the server. */
  const serverId = (key: string): string | null => {
    if (unsaved[key]) return null;
    if (!isPlaceholder(key)) return key;
    return savedIdOf[key] ?? null;
  };

  const inputFor = (step: LiveStep, after: MulticamAnnotation[]): MulticamAnnotationWrite | null => {
    if (step.kind === 'add') return toMutationInput(draftFromAnnotation(step.annotation));
    if (step.kind === 'delete') return null;
    const current = after.find((annotation) => keyOf(annotation.id) === step.key);
    return current ? toMutationInput(draftFromAnnotation(current)) : null;
  };

  /** The entry no longer includes a step that was not saved. */
  const prune = (job: Job) => {
    const { entry } = job.action;
    if (!entry) return;
    entry.steps = entry.steps.filter((step) => step !== job.step);
    if (entry.steps.length === 0) undoStack = undoStack.filter((candidate) => candidate !== entry);
  };

  /** Reverts the local change of a step that did not reach the server. */
  const rollBack = (job: Job) => {
    const { step } = job;
    if (step.kind === 'add') {
      unsaved[step.key] = true;
      deps.updateAnnotations((list) => list.filter((annotation) => keyOf(annotation.id) !== step.key));
    } else if (step.kind === 'retarget') {
      if (seqOf[step.key] === job.seq) {
        deps.updateAnnotations((list) => list.map((annotation) => (
          keyOf(annotation.id) === step.key ? { ...annotation, targetAngleKey: step.from } : annotation
        )));
      }
    } else {
      notDeleted[step.key] = true;
      const id = serverId(step.key);
      if (id && seqOf[step.key] === job.seq) {
        deps.updateAnnotations((list) => (list.some((annotation) => keyOf(annotation.id) === step.key)
          ? list
          : sortAnnotations([...list, { ...step.annotation, id }])));
      }
    }
    prune(job);
  };

  /** A cut added live now has its server id; requests queued behind it use it. */
  const adopt = (key: string, saved: MulticamAnnotation) => {
    savedIdOf[key] = saved.id;
    placeholderOf[saved.id] = key;
    // Later live changes may already have changed the cut: keep them, take the id.
    deps.updateAnnotations((list) => list.map((annotation) => (
      annotation.id === key
        ? { ...annotation, id: saved.id, multicamSetId: saved.multicamSetId, createdAt: saved.createdAt, updatedAt: saved.updatedAt }
        : annotation
    )));
  };

  const execute = async (job: Job): Promise<JobOutcome> => {
    const { step, action } = job;
    if (action.stopped) return { type: 'skipped', reason: 'stopped' };
    if (step.kind === 'add') {
      if (step.restores && notDeleted[step.restores]) return { type: 'skipped', reason: 'kept' };
      const multicamSetId = deps.getContext().multicamSetId;
      if (!multicamSetId || !job.input) throw new Error('This set could not be saved to.');
      const { endTimeMs, ...rest } = job.input;
      const created = await deps.api.add(multicamSetId, endTimeMs === null ? rest : { ...rest, endTimeMs });
      const [saved] = normalizeAnnotations(created ? [created] : []);
      if (!saved) throw new Error('The saved cut was not returned.');
      return { type: 'saved', annotation: saved };
    }
    const id = serverId(step.key);
    if (id === null) return { type: 'skipped', reason: 'unsaved' };
    if (step.kind === 'retarget') {
      if (!job.input) return { type: 'skipped', reason: 'gone' };
      await deps.api.update(id, job.input);
      return { type: 'saved' };
    }
    try {
      await deps.api.remove(id);
    } catch (error) {
      // Already gone (deleted in review, or in another tab): what the delete wanted.
      if (!/not found/i.test(messageOf(error))) throw error;
    }
    return { type: 'saved' };
  };

  const finish = (job: Job) => {
    pending[job.step.key] = Math.max(0, (pending[job.step.key] ?? 1) - 1);
  };

  const settle = (job: Job, outcome: JobOutcome) => {
    finish(job);
    const { step, action } = job;
    if (outcome.type === 'saved') {
      action.saved += 1;
      if (step.kind === 'add' && outcome.annotation) adopt(step.key, outcome.annotation);
    } else if (outcome.reason === 'stopped') {
      rollBack(job);
    } else {
      if (outcome.reason === 'kept' && step.kind === 'add') {
        // The cut it would bring back is still there; drop the copy.
        unsaved[step.key] = true;
        deps.updateAnnotations((list) => list.filter((annotation) => keyOf(annotation.id) !== step.key));
      }
      prune(job);
    }
    changed();
  };

  const fail = (job: Job, error: unknown) => {
    finish(job);
    const { action, step } = job;
    action.stopped = true;
    rollBack(job);
    // Nothing of a failed undo was saved: it can be tried again.
    if (action.undoOf && action.saved === 0 && !undoStack.includes(action.undoOf)) {
      undoStack.push(action.undoOf);
    }
    deps.onSaveError(error, step.kind === 'add' && !step.restores ? 'add-cut' : 'update');
    changed();
  };

  /** Applies the steps at once, then saves them one request each, in order. */
  const run = (steps: LiveStep[], entry: UndoEntry | null, undoOf: UndoEntry | null) => {
    const action: Action = { stopped: false, saved: 0, entry, undoOf };
    deps.updateAnnotations((list) => applyLiveSteps(list, steps, keyOf));
    const after = deps.getAnnotations();
    steps.forEach((step) => {
      seqOf[step.key] = (seqOf[step.key] ?? 0) + 1;
      pending[step.key] = (pending[step.key] ?? 0) + 1;
      const job: Job = { action, step, seq: seqOf[step.key], input: inputFor(step, after) };
      // The bookkeeping runs inside the job's turn, so the next request only
      // starts once this one's id is known or its failure is handled.
      deps.runInOrder(async () => {
        let outcome: JobOutcome;
        try {
          outcome = await execute(job);
        } catch (error) {
          fail(job, error);
          throw error;
        }
        settle(job, outcome);
        return outcome;
      }).catch(() => undefined);
    });
    changed();
  };

  const press = ({ angleKey, time, duration, hasFootage }: LivePress): LivePressPlan => {
    const context = deps.getContext();
    if (!context.editable || !context.multicamSetId) {
      return { kind: 'blocked', reason: 'read-only', angleKey, time: Math.max(0, time) };
    }
    const plan = planLivePress({
      annotations: deps.getAnnotations(),
      angleKey,
      time,
      fps: context.fps,
      masterKey: context.masterKey,
      duration,
      angleKeys: context.angleKeys,
      hasFootage,
      take,
      keyOf,
    });
    if (plan.kind === 'blocked') return plan;

    const takeBefore: LiveTake = { lastPressTime: take.lastPressTime, ownKeys: take.ownKeys.slice() };
    const steps = stepsForPlan(plan, newKey, context.multicamSetId, keyOf);
    const own = steps.filter((step) => step.kind !== 'delete').map((step) => step.key);
    take = { lastPressTime: plan.time, ownKeys: take.ownKeys.concat(own) };
    if (steps.length === 0) return plan;

    const entry: UndoEntry = { plan, steps, takeId, takeBefore };
    undoStack.push(entry);
    if (undoStack.length > LIVE_UNDO_LIMIT) undoStack = undoStack.slice(undoStack.length - LIVE_UNDO_LIMIT);
    run(steps, entry, null);
    return plan;
  };

  const undo = (): LiveCutPlan | null => {
    if (!deps.getContext().editable) return null;
    const entry = undoStack.pop();
    if (!entry) return null;
    const inverse = invertLiveSteps(entry.steps, deps.getAnnotations(), newKey, keyOf);
    // Undone mid-take: the take carries on from its press before this one.
    if (entry.takeId === takeId) take = { lastPressTime: entry.takeBefore.lastPressTime, ownKeys: entry.takeBefore.ownKeys.slice() };
    if (inverse.length) run(inverse, null, entry);
    else changed();
    return entry.plan;
  };

  const endTake = () => {
    takeId += 1;
    take = emptyTake();
  };

  return {
    press,
    undo,
    endTake,
    undoDepth: () => undoStack.length,
    resetUndo: () => {
      undoStack = [];
      takeId += 1;
      take = emptyTake();
      changed();
    },
    isBusy: (id) => (pending[keyOf(id)] ?? 0) > 0,
    keyOf,
  };
};
